// reconSearch — the pure matcher behind the reconcile board's search box.
//
// Dawn knows one fact about the receipt she is hunting: a price, a vendor, or a
// person. She should not have to know which pile it landed in, nor whether the
// name she remembers is the one the bank printed. So this compares on FOLDED
// text: "Mcdonalds" has to reach a statement line reading "MCDONALD'S #4521" and
// a receipt vendor of "McDonald's", which it only does once apostrophes, spacing
// and store numbers stop counting.
//
// Two haystack classes, not one blob. Folding text drops the decimal point, so a
// shared blob would read $86.43 and $8,643.00 as "8643" and "864300" and hand her
// the wrong one. Amounts therefore keep their point and are compared separately.
// A term matches a charge if it lands in EITHER class, so digits still reach
// dates and card last4s through the text side, which is a feature: typing a date
// is a real way to narrow a statement.
//
// Nothing here names a category, a vendor or a card. Both products run this file
// and their charts of accounts are seeded per database, so a literal like
// "Travel Meals" would be a silent Firefleet assumption.

import { formatEventDate } from '../utils/date-utils';
import { exceptionTypeLabel, type ReconTxn } from './types';

/** Fold to bare alphanumerics: punctuation and spacing are noise the eye already ignores. */
export function normalizeText(v: unknown): string {
  if (v == null) return '';
  return String(v).toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Keep digits and the decimal point, so "86.43" stays distinguishable from "8643". */
export function normalizeNumber(v: unknown): string {
  if (v == null) return '';
  return String(v).replace(/[^0-9.]/g, '');
}

/** Absolute value at 2dp — the board shows charges unsigned, so the search matches what she reads. */
function amountKey(v: unknown): string {
  if (v == null || v === '') return '';
  const n = Number(v);
  return Number.isFinite(n) ? Math.abs(n).toFixed(2) : '';
}

/** Both spellings of a date: the ISO the API returns and the "Aug 12, 2026" the board renders. */
function dateKeys(v: string | null | undefined): string[] {
  if (!v) return [];
  const out = [v];
  try { out.push(formatEventDate(v)); } catch { /* unparseable date, ISO alone still searches */ }
  return out;
}

// Built once per charge, not once per keystroke. Every character she types would
// otherwise re-run two dayjs formats for every row on the statement, which is the
// dominant cost of the whole filter on a month-end import. Keyed on the txn object
// and WeakMap-held: `loadBoard` replaces the objects wholesale, so a reload cannot
// serve a stale entry and the old ones are collectable.
const haystackCache = new WeakMap<ReconTxn, { text: string[]; numeric: string[] }>();

/**
 * Everything about one charge a person might type, split by how it has to be
 * compared. Receipt-side fields ride the match, so a confirmed charge is
 * findable by its receipt's vendor and its bookkeeping labels, not only by the
 * cryptic string the bank printed.
 */
export function txnHaystacks(txn: ReconTxn): { text: string[]; numeric: string[] } {
  const cached = haystackCache.get(txn);
  if (cached) return cached;
  const m = txn.match;
  const text = [
    txn.description,
    txn.card_label,
    txn.card_last4,
    ...dateKeys(txn.txn_date),
    m?.vendor_name,
    m?.receipt_category_label,
    m?.receipt_purchase_class_label,
    m?.receipt_card_last4,
    ...dateKeys(m?.receipt_date),
    txn.exception ? exceptionTypeLabel(txn.exception.exception_type) : null,
  ].map(normalizeText).filter(Boolean);

  const numeric = [amountKey(txn.amount), amountKey(m?.receipt_total)].filter(Boolean);

  const built = { text, numeric };
  haystackCache.set(txn, built);
  return built;
}

/**
 * Does this charge satisfy the query? Terms split on whitespace and are ANDed,
 * each free to land in a different field — "mcdonalds 86.43" finds the one
 * McDonald's charge at that price even though vendor and amount are separate
 * facts. An empty query matches everything, so callers can pass it through
 * unconditionally.
 */
export function txnMatchesQuery(txn: ReconTxn, query: string): boolean {
  const terms = query.trim().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const { text, numeric } = txnHaystacks(txn);
  return terms.every((term) => {
    const t = normalizeText(term);
    if (t && text.some((h) => h.includes(t))) return true;
    const n = normalizeNumber(term);
    return n !== '' && n !== '.' && numeric.some((h) => h.includes(n));
  });
}
